import { Injectable, inject } from '@angular/core'

import { Observable, forkJoin, map, of, switchMap } from 'rxjs'

import { BulkDataModelAttribute } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { DataModelApiService } from '@flowx/designer/shared/api/data-model.api'
import { ProcessDataModelApiService } from '@flowx/designer/shared/api/process-data-model.api'
import {
  ApplicationResourceType,
  mapResource,
} from '@flowx/designer/shared/entities/application.entity'

import { DataModelDraft, DataModelDraftItem } from '../../../types/ai.types'
import { DataModelItemStatus } from '../../designer/dictionary/designer.dictionary'
import { DesignerService } from '../../designer/services/designer.service'

type DataModelEntityNode = {
  identifier: string
  // `root`/`flowxUuid` only exist on process entities; project data types carry neither.
  root?: boolean
  flowxUuid?: string
  attributes: { name: string; reference?: { targetFlowxUuid?: string } }[]
}

/**
 * Applies the developer `data_model_changes` draft additively onto the process data model: NEW keys
 * via the path-bulk endpoint, project/library types as OBJECT reference attributes. Mirrors
 * {@link AiDesignerEffects}'s `saveDataModelDraft` + `#applyReferencedDataModelItems`.
 */
@Injectable({ providedIn: 'root' })
export class DeveloperDataModelService {
  #processDataModelApi = inject(ProcessDataModelApiService)
  #dataModelApi = inject(DataModelApiService)

  applyDataModelDraft(
    resourceId: string,
    resourceEntities: DataModelEntityNode[],
    draft: DataModelDraft
  ): Observable<unknown> {
    const bulkAttributes: BulkDataModelAttribute[] = DesignerService.prepareDataModelForBulkCreate(
      draft.new.map((item) => ({ ...item, state: DataModelItemStatus.CREATED }))
    ).map(({ key, type, label, multiInstance, properties }) => ({
      path: key,
      type,
      description: label,
      multiInstance,
      properties,
    }))

    const referencedItems = (draft.referenced ?? []).filter((item) => !item.inactive)

    const bulkSave$: Observable<unknown> = bulkAttributes.length
      ? this.#processDataModelApi.bulkAddAttributesFromPath(resourceId, bulkAttributes)
      : of(null)

    const referencedSave$: Observable<unknown> = referencedItems.length
      ? this.#applyReferencedItems(resourceId, resourceEntities, referencedItems)
      : of(null)

    // Primitives first, then references (which depend on the process root entity existing).
    return bulkSave$.pipe(switchMap(() => referencedSave$))
  }

  // Ensure the process root entity exists, then add each referenced item as an OBJECT attribute
  // carrying the `{ targetResourceDefinitionId, appId }` reference arm. The reference key holds the
  // object's full path; walk the parent segments from root (following each OBJECT attribute's
  // targetFlowxUuid) so e.g. `application.client` lands inside the `application` object, falling back
  // to root when a segment can't be resolved.
  #applyReferencedItems(
    resourceId: string,
    resourceEntities: DataModelEntityNode[],
    referencedItems: DataModelDraftItem[]
  ): Observable<unknown> {
    const options = { resourceType: ApplicationResourceType.Process, resourceId }
    const entities = resourceEntities ?? []
    const rootEntity = entities.find((entity) => entity.root)

    const resolveTargetEntityId = (key: string | undefined, rootId: string): string => {
      const parents = (key ?? '').split('.').slice(0, -1)
      let entity: DataModelEntityNode | undefined = rootEntity
      for (const segment of parents) {
        const childUuid = entity?.attributes?.find((a) => a.name === segment)?.reference
          ?.targetFlowxUuid
        entity = childUuid ? entities.find((e) => e.flowxUuid === childUuid) : undefined
        if (!entity) {
          return rootId
        }
      }
      return entity?.identifier ?? rootId
    }

    const rootEntityId$: Observable<string> = rootEntity?.identifier
      ? of(rootEntity.identifier)
      : this.#dataModelApi
          .addDataModelEntity(options, { name: '', root: true })
          .pipe(map((response) => mapResource(response).id))

    return rootEntityId$.pipe(
      switchMap((rootId) =>
        forkJoin(
          referencedItems.map((item) =>
            this.#dataModelApi.addAttributeToDataModelEntity(
              options,
              resolveTargetEntityId(item.key, rootId),
              DesignerService.buildReferencedAttributeParams(item)
            )
          )
        )
      )
    )
  }
}
